import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { getReason, useReasonReducer } from "@/main/store/reason";
import { useCountryReducer } from "@/main/store/country";

import { quoteApi } from "@/main/api";

import { getDefaultCountryOptionDrop, getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { convertStringForBoolean } from "@/main/services/utilsString";
import { currencyToNumber, formatCurrency } from "@/main/services/utilsNumber";
import { ReasonTypeEnum } from "@/main/services/enum/ReasonEnum";

import { CorrectCartItemPriceRequest, ReasonFilterRequest } from "@/main/models/request";
import { KeyValueDefault } from "@/main/models/common";
import { QuoteGetItemConditionsWrapper, QuoteProcurementPartInfoResponse } from "@/main/models/response";

import Box from "@/main/components/shared/box";
import KogAutoCompleteControlled from "@/main/components/shared/inputAutoComplete/autoCompleteControlled";
import KogInputTextControlledNew from "@/main/components/shared/inputText/inputTextControlledNew";
import KogInputAmountControlled from "@/main/components/shared/inputText/inputAmountControlled";
import SecondaryButton from "@/main/components/shared/button/secondaryButton";
import Button from "@/main/components/shared/button/button";

import * as S from "@/main/screens/order/viewOrder/components/modalEditPart/styles";

export interface ICorrectPricePart {
	guidQuoteSupplierItem: string;
	price?: number;
	quoteProcurementPartInfo?: QuoteProcurementPartInfoResponse;
}

interface IModalCorrectCartPriceProps {
	part: ICorrectPricePart;
	supplierName: string;
	guidScreen: string;
	onCorrected: (result: QuoteGetItemConditionsWrapper) => void;
	closeModal: () => void;
}

interface ICorrectPriceForm {
	newPrice: string;
	reason: KeyValueDefault | null;
	observation: string;
}

function ModalCorrectCartPrice({
	part,
	supplierName,
	guidScreen,
	onCorrected,
	closeModal,
}: IModalCorrectCartPriceProps) {
	const dispatch = useDispatch();
	const { reason: reasonReducer } = useReasonReducer();
	const countryReducer = useCountryReducer();
	const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countryReducer.countriesUser.data);

	const [isLoading, setIsLoading] = useState<boolean>(false);

	const partInfo = part.quoteProcurementPartInfo;
	const partName = partInfo?.partName ?? partInfo?.quoteItemDescription;

	const { control, setValue, handleSubmit, reset, watch } = useForm<ICorrectPriceForm>({
		mode: "onChange",
		defaultValues: {
			newPrice: isNullUndefined(part.price) ? "" : formatCurrency(part.price, true),
			reason: null,
			observation: "",
		},
	});
	const isObservationRequired = convertStringForBoolean(watch("reason")?.default);

	useEffect(() => {
		if (optionDefaultCountryDrop === null) return;

		const requestReason: ReasonFilterRequest = {
			guidCountry: String(optionDefaultCountryDrop.key),
			idReasonType: ReasonTypeEnum.EditPrice,
		};
		dispatch(getReason(requestReason));
	}, []);

	function onSubmit(data: ICorrectPriceForm) {
		const request: CorrectCartItemPriceRequest = {
			guidQuoteSupplierItem: String(part.guidQuoteSupplierItem),
			newPrice: currencyToNumber(data.newPrice) ?? 0,
			guidReason: String(data.reason?.key),
			reasonDescription: isObservationRequired ? data.observation : undefined,
			guidScreen,
		};

		setIsLoading(true);
		quoteApi
			.correctCartItemPrice(request)
			.then((response) => {
				onCorrected(response.data.result);
				toast.success(getTranslateByKey("information.priceCorrectedSuccessfully"));
				closeModal();
			})
			.catch(() => undefined)
			.finally(() => setIsLoading(false));
	}

	function handleBack() {
		reset();
		closeModal();
	}

	return (
		<Box>
			<S.BoxHeaderEditPart>
				<S.Container>
					{supplierName && (
						<S.ContainerRow>
							<S.Label>{supplierName}</S.Label>
						</S.ContainerRow>
					)}
					{(partName || partInfo?.partType) && (
						<S.ContainerRow>
							{partName && <S.Label>{partName}</S.Label>}
							{partInfo?.partType && (
								<S.Text>
									{` - ${partInfo.partType}`}
									{partInfo.partBrand && ` (${partInfo.partBrand})`}
								</S.Text>
							)}
						</S.ContainerRow>
					)}
					{partInfo?.supplierInventoryPartTitle && (
						<S.ContainerRow>
							<S.Text>{partInfo.supplierInventoryPartTitle}</S.Text>
						</S.ContainerRow>
					)}
					{!isNullUndefined(part.price) && (
						<S.ContainerRow>
							<S.Text>{`${getTranslateByKey("information.currentPrice")}: `}</S.Text>
							<S.Label>{formatCurrency(part.price, true)}</S.Label>
						</S.ContainerRow>
					)}
				</S.Container>
			</S.BoxHeaderEditPart>

			<form onSubmit={handleSubmit(onSubmit)} noValidate>
				<KogInputAmountControlled
					required
					typeInput="filled"
					id="id-correct-cart-price-input-new-price"
					name="newPrice"
					label={getTranslateByKey("information.newPrice")}
					control={control}
					controllerProps={{ rules: { maxLength: 10 } }}
				/>
				<KogAutoCompleteControlled
					className="ignore-click"
					id="id-correct-cart-price-select-reason"
					required
					nameCustom="reason"
					label={getTranslateByKey("information.reason")}
					control={control}
					setValueCustom={setValue}
					options={reasonReducer.data}
					isLoading={reasonReducer.isLoading}
					rulesCustom={{ required: true }}
					onChangeCustom={(option) => {
						if (option === null) return;
						setValue("observation", "");
					}}
					style={{ marginTop: 10, marginBottom: 10 }}
				/>
				{isObservationRequired && (
					<KogInputTextControlledNew
						required
						autoFocus
						id="id-correct-cart-price-input-description"
						name="observation"
						label={getTranslateByKey("information.observation")}
						control={control}
					/>
				)}

				<S.ButtonGroup>
					<SecondaryButton type="button" id="id-correct-cart-price-btn-back" disabled={isLoading} onClick={handleBack}>
						{getTranslateByKey("information.back")}
					</SecondaryButton>
					<Button type="submit" id="id-correct-cart-price-btn-save" isLoading={isLoading}>
						{getTranslateByKey("information.save")}
					</Button>
				</S.ButtonGroup>
			</form>
		</Box>
	);
}

export default ModalCorrectCartPrice;
